import { useEffect, useRef, useState } from 'react';

type Options = { delayMs?: number; minDisplayMs?: number };

const DEFAULT_DELAY = 250;
const DEFAULT_MIN_DISPLAY = 600;

/**
 * Gate a `loading` boolean behind a delay + minimum-display window so:
 *   - Loads that resolve in under `delayMs` never show the loader.
 *   - Loads that resolve in under `delayMs + minDisplayMs` keep the
 *     loader visible for the full `minDisplayMs` to avoid a flash.
 *   - Longer loads track `loading` directly.
 *
 * Defaults: delayMs = 250, minDisplayMs = 600.
 *
 * Implementation note: timing is driven entirely by `setTimeout`, never
 * `Date.now()`. That keeps the hook deterministic under fake timers and
 * resilient to clock drift.
 */
export function useDelayedLoader(
  loading: boolean,
  opts: Options = {},
): boolean {
  const delayMs = opts.delayMs ?? DEFAULT_DELAY;
  const minDisplayMs = opts.minDisplayMs ?? DEFAULT_MIN_DISPLAY;
  const [show, setShow] = useState(false);

  // Tracks whether the minimum-display window has elapsed since the loader
  // most recently appeared. While `false`, the loader stays visible even if
  // `loading` flips false. Once `true`, hide decisions follow `loading`.
  const minDisplayElapsed = useRef(true);

  // Mirror the latest `loading` value into a ref so the min-display timer
  // callback can decide what to do when it fires without reading stale state.
  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  });

  const delayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const minDisplayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (loading) {
      // Schedule the loader to appear after the delay window, unless it's
      // already shown or already scheduled.
      if (delayTimer.current == null && !show) {
        delayTimer.current = setTimeout(() => {
          delayTimer.current = null;
          setShow(true);
          minDisplayElapsed.current = false;
          minDisplayTimer.current = setTimeout(() => {
            minDisplayElapsed.current = true;
            minDisplayTimer.current = null;
            // If `loading` flipped false during the min-display window, hide
            // now that the window has elapsed.
            if (!loadingRef.current) {
              setShow(false);
            }
          }, minDisplayMs);
        }, delayMs);
      }
      return;
    }

    // loading === false
    if (delayTimer.current != null) {
      // Loader hadn't appeared yet — cancel the appearance entirely.
      clearTimeout(delayTimer.current);
      delayTimer.current = null;
      return;
    }
    if (show && minDisplayElapsed.current) {
      // Past the min-display window — hide immediately.
      setShow(false);
    }
    // Otherwise, wait for the min-display timer to fire and let it decide.
  }, [loading, show, delayMs, minDisplayMs]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (delayTimer.current != null) clearTimeout(delayTimer.current);
      if (minDisplayTimer.current != null) clearTimeout(minDisplayTimer.current);
    };
  }, []);

  return show;
}
